"use server"

import { sdk } from "@lib/config"
import medusaError from "@lib/util/medusa-error"
import compareAddresses from "@lib/util/compare-addresses"
import { normalizeTaiwanMobile } from "@lib/util/taiwan-mobile"
import { HttpTypes } from "@medusajs/types"
import { revalidateTag } from "next/cache"
import { redirect } from "next/navigation"

import { restoreCustomerCart, retrieveCart } from "./cart"
import {
  getAuthHeaders,
  getCacheTag,
  getCartId,
  removeAuthToken,
  removeCartId,
  setAuthToken,
} from "./cookies"

/**
 * 目前登入的會員。
 *
 * ⚠️ **刻意不快取。** 這是身分資料,而且它會從**後台**被改動 —— 前台沒有任何
 * 地方會在後台刪除會員時去失效 `customers` 這個 tag。原本用 `force-cache`
 * 的結果是:會員在後台已經刪掉了,前台的個人資料頁還照常顯示他的姓名、電話、
 * 訂單 —— 看起來像還登入著。
 *
 * 這跟付款方式清單那次是同一類問題(見 CHANGES C-?「結帳頁看不到 LINE Pay」),
 * 但嚴重度不同:那邊是少一個選項,這邊是**已經不存在的帳號看起來還活著**。
 * 所以不是縮短快取時間,而是完全不快取 —— 每頁多一次後端查詢,換身分狀態
 * 永遠是真的。
 */
export const retrieveCustomer =
  async (): Promise<HttpTypes.StoreCustomer | null> => {
    const authHeaders = await getAuthHeaders()

    if (!authHeaders) return null

    const headers = {
      ...authHeaders,
    }

    return await sdk.client
      .fetch<{ customer: HttpTypes.StoreCustomer }>(`/store/customers/me`, {
        method: "GET",
        query: {
          fields: "*orders",
        },
        headers,
        cache: "no-store",
      })
      .then(({ customer }) => customer)
      .catch(async (error: any) => {
        // 404 = token 有效但會員已不存在(例:測試機清空後)。cookie 留著
        // 使用者會卡在「按登出沒反應、按登入被當成已登入」的夾縫(issue #11)
        // —— 直接清掉,讓他乾淨地變回訪客。RSC 渲染中不能改 cookie
        // (Next 會丟例外),那種情境就吞掉,等下一個 server action 再清。
        if (error?.status === 404) {
          await removeAuthToken().catch(() => {})
        }
        return null
      })
  }

export const updateCustomer = async (body: HttpTypes.StoreUpdateCustomer) => {
  const headers = {
    ...(await getAuthHeaders()),
  }

  const updateRes = await sdk.store.customer
    .update(body, {}, headers)
    .then(({ customer }) => customer)
    .catch(medusaError)

  const cacheTag = await getCacheTag("customers")
  revalidateTag(cacheTag)

  return updateRes
}

/**
 * 改自己的密碼。只有綁了 emailpass 的會員可用 —— 手機/LINE/Google 註冊的
 * 沒有密碼,前台靠 listLinkedProviders 判斷要不要顯示表單,後端也會再擋一次。
 *
 * 走自訂的 /store/account/password:內建的 /auth/customer/emailpass/update
 * 只收 reset token,登入 session 打它是 401(說明見後端該檔)。
 */
export async function updateCustomerPassword(
  _currentState: Record<string, unknown>,
  formData: FormData
) {
  const headers = {
    ...(await getAuthHeaders()),
  }

  return sdk.client
    .fetch("/store/account/password", {
      method: "POST",
      headers,
      body: {
        current_password: formData.get("old_password"),
        new_password: formData.get("new_password"),
        confirm_password: formData.get("confirm_password"),
      },
    })
    .then(() => ({ success: true, error: null }))
    .catch((err) => {
      // FetchError 的 message 就是後端 JSON 的 message(已是給人看的中文)
      return { success: false, error: err?.message ?? String(err) }
    })
}

/**
 * 註冊前檢查 email / 手機是否已被使用(issue #3)。
 * 表單 onBlur 打,錯誤一律當「不知道」回 null —— 檢查失敗不該擋人打字,
 * 真正的把關在 signup 與後端。
 */
export async function checkRegisterAvailability(input: {
  email?: string
  phone?: string
}) {
  return sdk.client
    .fetch<{ email_taken: boolean | null; phone_taken: boolean | null }>(
      "/store/register-check",
      { method: "GET", query: input, cache: "no-store" }
    )
    .catch(() => ({ email_taken: null, phone_taken: null }))
}

export async function signup(_currentState: unknown, formData: FormData) {
  const password = formData.get("password") as string

  // 表單有 required + pattern,但那擋不住繞過前端的請求。
  // 正規化到 09xxxxxxxx 再存,之後手機驗證是拿這個值去比對的。
  const phone = normalizeTaiwanMobile((formData.get("phone") as string) ?? "")

  if (!phone) {
    return "請輸入正確的行動電話(09 開頭共 10 碼)"
  }

  // 與改密碼(/store/account/password)同一條線:註冊收 3 碼、改密碼要 8 碼
  // 是自相矛盾的要求
  if ((password ?? "").length < 8) {
    return "密碼至少要 8 個字元"
  }

  const customerForm = {
    email: formData.get("email") as string,
    first_name: formData.get("first_name") as string,
    last_name: formData.get("last_name") as string,
    phone,
  }

  // 送出前再驗一次重複 —— onBlur 的警告擋不住無視警告直接按送出的人,
  // 而 Medusa 撞到重複時的錯誤訊息是英文的
  const taken = await checkRegisterAvailability({
    email: customerForm.email,
    phone,
  })

  if (taken?.email_taken) {
    return "這個電子郵件已經註冊過,請直接登入或用「忘記密碼」"
  }

  if (taken?.phone_taken) {
    return "這個手機號碼已經註冊過,可改用手機驗證碼登入"
  }

  try {
    const token = await sdk.auth.register("customer", "emailpass", {
      email: customerForm.email,
      password: password,
    })

    await setAuthToken(token as string)

    const headers = {
      ...(await getAuthHeaders()),
    }

    const { customer: createdCustomer } = await sdk.store.customer.create(
      customerForm,
      {},
      headers
    )

    const loginToken = await sdk.auth.login("customer", "emailpass", {
      email: customerForm.email,
      password,
    })

    await setAuthToken(loginToken as string)

    const customerCacheTag = await getCacheTag("customers")
    revalidateTag(customerCacheTag)

    await transferCart()

    return createdCustomer
  } catch (error: any) {
    return error.toString()
  }
}

export async function login(_currentState: unknown, formData: FormData) {
  const email = formData.get("email") as string
  const password = formData.get("password") as string

  try {
    await sdk.auth
      .login("customer", "emailpass", { email, password })
      .then(async (token) => {
        await setAuthToken(token as string)
        const customerCacheTag = await getCacheTag("customers")
        revalidateTag(customerCacheTag)
      })
  } catch (error: any) {
    return error.toString()
  }

  try {
    await transferCart()
  } catch (error: any) {
    return error.toString()
  }
}

export async function signout(countryCode: string) {
  await sdk.auth.logout()

  await removeAuthToken()

  const customerCacheTag = await getCacheTag("customers")
  revalidateTag(customerCacheTag)

  await removeCartId()

  const cartCacheTag = await getCacheTag("carts")
  revalidateTag(cartCacheTag)

  redirect(`/${countryCode}/account`)
}

/**
 * 登入後把購物車掛到會員身上。
 *
 * ⚠️ **Medusa 的 transferCart 會用 `customer.email` 覆寫 `cart.email`**
 * (`transferCartCustomerWorkflow` 裡是無條件的 update)。手機註冊的會員
 * `customer.email` 是 null —— 訪客剛在結帳頁填好的 email 會在登入那一刻被洗掉,
 * 而且畫面上不會有任何跡象。
 *
 * 所以轉移前先記下來,轉移後發現變空就補回去。
 *
 * 為什麼不無條件補寫:訪客填了 A、但登入的會員 email 是 B 時,無條件補寫會把
 * B 蓋回 A。多讀一次確認比較安全,而這條路徑一次結帳只會走一遍。
 *
 * 補寫必須在 `revalidateTag` **之前** —— 否則畫面會先拿到 email 是空的那一版,
 * 閃一下才變回來。
 */
export async function transferCart() {
  const cartId = await getCartId()

  if (!cartId) {
    // 沒有匿名車 = 沒有東西會被蓋掉 —— 這時把他上次沒結完的車接回來。
    // 登出必定走到這裡(signout 會 removeCartId),見 restoreCustomerCart()。
    await restoreCustomerCart()
    return
  }

  const headers = await getAuthHeaders()
  const before = await retrieveCart(cartId, "id,email,*items").catch(() => null)
  const typedEmail = before?.email ?? null

  /**
   * 手上這台是空的 —— 一樣沒有東西會被蓋掉,優先接回舊的那台。
   *
   * ⚠️ 順序不能顛倒:先 transfer 再找回,那台空車會變成「他最後動過的車」,
   * 舊的那台就永遠排在後面了(後端取 `updated_at` 最新的一台)。
   *
   * 接回來的車本來就掛在這個會員身上,不需要再 transfer 一次。
   */
  if (!before?.items?.length && (await restoreCustomerCart())) {
    const customerCacheTag = await getCacheTag("customers")
    revalidateTag(customerCacheTag)
    return
  }

  await sdk.store.cart.transferCart(cartId, {}, headers)

  if (typedEmail) {
    const after = await retrieveCart(cartId, "id,email").catch(() => null)

    if (!after?.email) {
      await sdk.store.cart
        .update(cartId, { email: typedEmail }, {}, headers)
        .catch(() => {
          // 補不回去不該擋住登入 —— 使用者可以在結帳頁再填一次
        })
    }
  }

  const cartCacheTag = await getCacheTag("carts")
  revalidateTag(cartCacheTag)
}

export const addCustomerAddress = async (
  currentState: Record<string, unknown>,
  formData: FormData
): Promise<any> => {
  const isDefaultBilling = (currentState.isDefaultBilling as boolean) || false
  const isDefaultShipping = (currentState.isDefaultShipping as boolean) || false

  const address = {
    first_name: formData.get("first_name") as string,
    last_name: formData.get("last_name") as string,
    company: formData.get("company") as string,
    address_1: formData.get("address_1") as string,
    address_2: formData.get("address_2") as string,
    city: formData.get("city") as string,
    postal_code: formData.get("postal_code") as string,
    province: formData.get("province") as string,
    country_code: formData.get("country_code") as string,
    phone: formData.get("phone") as string,
    is_default_billing: isDefaultBilling,
    is_default_shipping: isDefaultShipping,
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  return sdk.store.customer
    .createAddress(address, {}, headers)
    .then(async ({ customer }) => {
      const customerCacheTag = await getCacheTag("customers")
      revalidateTag(customerCacheTag)
      return { success: true, error: null }
    })
    .catch((err) => {
      return { success: false, error: err.toString() }
    })
}

// compareAddresses 內部用 lodash isEqual 比對,null / undefined / "" 會被當成
// 三種不同的值。order address 與 customer address 對「沒填」的表示法不一致,
// 比對前先統一成空字串。
const normalizeAddress = (address: Record<string, any>) =>
  Object.fromEntries(
    Object.entries(address).map(([key, value]) => [key, value ?? ""])
  )

/**
 * 把訂單的收件地址複製一份到會員地址簿。
 *
 * Medusa 下單時只會把地址寫進 order_address,不會回寫 customer_address,
 * 所以顧客結帳填的地址不會出現在 /account/addresses。這支補上那一段。
 *
 * 由 placeOrder 在轉址前呼叫。訪客結帳(沒有登入 token)直接略過。
 * 整段吞錯誤只記 log —— 訂單已經成立,地址簿存不進去不該影響下單。
 */
export const saveOrderShippingAddressToAccount = async (
  order: HttpTypes.StoreOrder
): Promise<void> => {
  const address = order.shipping_address

  if (!address) {
    return
  }

  /**
   * 真正的條件是「有沒有登入的會員」,而那件事 authorization header 就證明了 ——
   * 地址簿 API 本來就只認 token、寫進的一定是那個人的地址簿。
   *
   * 刻意**不**用 `order.customer_id` 當判斷:`cart.complete` 的預設欄位裡
   * 沒有它(有 shipping_address、就是沒有 customer_id),曾經因此整個功能
   * 靜默失效 —— 在 try/catch 之前 return,連一行 log 都沒有。
   * 現在 placeOrder 會明確要求那個欄位,但這裡也不再依賴它。
   */
  const authHeaders = await getAuthHeaders()

  if (!("authorization" in authHeaders)) {
    /**
     * 🔴 訂單有 `customer_id`、這次請求卻沒有 token —— 那**不是**訪客結帳,
     * 是異常,而且是這一整支唯一會漏掉真會員的路徑。實際發生過:LINE Pay 把
     * 顧客導回 `http://10.50.1.100`(而他是在 `https://dev.vibe-select.com`
     * 結帳的),origin 一換 cookie 就不會送,於是這裡把一位會員判成訪客,
     * 靜默 return —— 三張訂單的地址都沒進地址簿,而畫面與 log 都沒有任何線索
     * (CHANGES.md C-42)。
     *
     * 根因修在 `linepay.ts` / `line.ts` 的 `storefront_url`,但那類「回跳網址
     * 指到別的 origin」的問題會再發生,所以這裡留一條看得見的痕跡。
     */
    if (order.customer_id) {
      console.warn(
        `[address] 訂單 ${order.id} 掛在會員 ${order.customer_id} 名下,` +
          `但這次請求沒有登入 token —— 地址沒有寫進地址簿。` +
          `多半是回跳網址指到了另一個 origin(cookie 不會跨 origin 送)。`
      )
      return
    }

    // 真的訪客結帳 —— 這是正常情況,不留 log(留了會變成每張訪客訂單一行雜訊)
    return
  }

  try {
    // 用 listAddress 而不是 retrieveCustomer —— 後者走 Next cache,
    // 去重要比對的是當下真實的地址簿內容。
    const { addresses: existing } = await sdk.store.customer.listAddress(
      { limit: 100 },
      authHeaders
    )

    // order address 的空欄位是 null、地址簿存的可能是空字串,
    // 不先正規化的話 compareAddresses 內的 isEqual 會判定成不同筆。
    const alreadySaved = existing.some((saved) =>
      compareAddresses(normalizeAddress(saved), normalizeAddress(address))
    )

    if (alreadySaved) {
      console.info(`[address] 訂單 ${order.id} 的收件地址已在地址簿裡,略過`)
      return
    }

    await sdk.store.customer.createAddress(
      {
        first_name: address.first_name ?? "",
        last_name: address.last_name ?? "",
        company: address.company ?? "",
        address_1: address.address_1 ?? "",
        address_2: address.address_2 ?? "",
        city: address.city ?? "",
        province: address.province ?? "",
        postal_code: address.postal_code ?? "",
        country_code: address.country_code ?? "",
        phone: address.phone ?? "",
        // 地址簿原本是空的才設預設 —— customer_address 有 partial unique
        // index,一個會員只能有一筆 is_default_shipping。
        is_default_shipping: existing.length === 0,
        metadata: { source: "checkout", order_id: order.id },
      },
      {},
      authHeaders
    )

    // 成功也要留一行。這個功能曾經整條靜默失效了很久,原因正是
    // 「成功與失敗都不留痕跡」—— 沒有人有辦法知道它到底跑了沒有。
    console.info(`[address] 訂單 ${order.id} 的收件地址已複製到地址簿`)

    const customerCacheTag = await getCacheTag("customers")
    revalidateTag(customerCacheTag)
  } catch (err) {
    console.error("[address] 結帳地址複製到地址簿失敗:", err)
  }
}

export const deleteCustomerAddress = async (
  addressId: string
): Promise<void> => {
  const headers = {
    ...(await getAuthHeaders()),
  }

  await sdk.store.customer
    .deleteAddress(addressId, headers)
    .then(async () => {
      const customerCacheTag = await getCacheTag("customers")
      revalidateTag(customerCacheTag)
      return { success: true, error: null }
    })
    .catch((err) => {
      return { success: false, error: err.toString() }
    })
}

export const updateCustomerAddress = async (
  currentState: Record<string, unknown>,
  formData: FormData
): Promise<any> => {
  const addressId =
    (currentState.addressId as string) || (formData.get("addressId") as string)

  if (!addressId) {
    return { success: false, error: "Address ID is required" }
  }

  const address = {
    first_name: formData.get("first_name") as string,
    last_name: formData.get("last_name") as string,
    company: formData.get("company") as string,
    address_1: formData.get("address_1") as string,
    address_2: formData.get("address_2") as string,
    city: formData.get("city") as string,
    postal_code: formData.get("postal_code") as string,
    province: formData.get("province") as string,
    country_code: formData.get("country_code") as string,
  } as HttpTypes.StoreUpdateCustomerAddress

  const phone = formData.get("phone") as string

  if (phone) {
    address.phone = phone
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  return sdk.store.customer
    .updateAddress(addressId, address, {}, headers)
    .then(async () => {
      const customerCacheTag = await getCacheTag("customers")
      revalidateTag(customerCacheTag)
      return { success: true, error: null }
    })
    .catch((err) => {
      return { success: false, error: err.toString() }
    })
}

/**
 * 忘記密碼:請後端寄重設信。
 *
 * 後端的 workflow 刻意不透露帳號存在與否(永遠回 201),所以這裡
 * **成功與否都回同一句話** —— 回「查無此帳號」等於幫人枚舉會員信箱。
 */
export async function requestPasswordReset(
  _currentState: unknown,
  formData: FormData
): Promise<{ done: boolean; message: string }> {
  const email = String(formData.get("email") ?? "").trim()

  if (!email) {
    return { done: false, message: "請輸入電子郵件" }
  }

  try {
    await sdk.auth.resetPassword("customer", "emailpass", {
      identifier: email,
    })
  } catch {
    // 網路層的錯也吞掉 —— 回應必須與成功不可區分
  }

  return {
    done: true,
    message:
      "如果這個信箱有帳號,重設密碼的信已寄出(15 分鐘內有效)。" +
      "沒收到的話,確認信箱拼字或看垃圾信匣。",
  }
}

/** 帶著信裡的 token 設定新密碼 */
export async function resetPasswordWithToken(
  _currentState: unknown,
  formData: FormData
): Promise<{ success: boolean; message: string }> {
  const token = String(formData.get("token") ?? "")
  const email = String(formData.get("email") ?? "").trim()
  const password = String(formData.get("password") ?? "")
  const repeat = String(formData.get("repeat_password") ?? "")

  if (!token || !email) {
    return {
      success: false,
      message: "連結不完整,請重新從信裡點一次,或再要一封重設信。",
    }
  }

  // 與後端 store/account/password 的規則一致(MIN_LENGTH = 8)
  if (password.length < 8) {
    return { success: false, message: "新密碼至少要 8 個字元。" }
  }

  if (password !== repeat) {
    return { success: false, message: "兩次輸入的密碼不一樣。" }
  }

  try {
    await sdk.auth.updateProvider(
      "customer",
      "emailpass",
      { email, password },
      token
    )
  } catch {
    // token 過期/已用過/被竄改,後端都回 401 —— 對使用者是同一件事
    return {
      success: false,
      message:
        "重設連結已失效(超過 15 分鐘或已使用過)。請回登入頁再按一次「忘記密碼」。",
    }
  }

  return { success: true, message: "密碼已重設,請用新密碼登入。" }
}
